import { useState } from 'react';
import { Edit2, Trash2, PersonStanding, BarChart3, ChevronDown } from 'lucide-react';
import { bodyweightApi } from '@/services/api';
import { ExerciseStatsPanel, ImageViewer } from '@/components/common';
import type { ExerciseStatsData } from '@/components/common/ExerciseStatsPanel';
import type { BodyweightExercise, BodyweightSubcategory } from '@/types';

const subcategoryLabels: Record<BodyweightSubcategory, { label: string; icon: string }> = {
  upper: { label: 'Felsőtest', icon: '💪' },
  lower: { label: 'Alsótest', icon: '🦵' },
  core: { label: 'Törzs', icon: '🎯' },
  complex: { label: 'Komplex', icon: '🔥' },
};

interface BodyweightGridCardProps {
  exercise: BodyweightExercise;
  onEdit: () => void;
  onDelete: () => void;
}

export const BodyweightGridCard = ({ exercise, onEdit, onDelete }: BodyweightGridCardProps) => {
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState<ExerciseStatsData | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [showDescription, setShowDescription] = useState(false);

  const subInfo = subcategoryLabels[exercise.subcategory];
  const hasDescription = !!(exercise.description || exercise.exerciseDescription);

  const loadStats = async () => {
    if (stats) {
      setShowStats(!showStats);
      return;
    }

    setLoadingStats(true);
    try {
      const response = await bodyweightApi.getStats(exercise.id, 20);
      if (response.success && response.data) {
        setStats(response.data);
        setShowStats(true);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Exercise Photo */}
      {exercise.photoUrl ? (
        <div className="bg-black cursor-pointer" onClick={() => setShowImageViewer(true)}>
          <img
            src={exercise.thumbnailUrl || exercise.photoUrl}
            alt={exercise.name}
            className="w-full h-[200px] object-contain"
          />
        </div>
      ) : (
        <div className="bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30 h-[200px] flex items-center justify-center">
          <PersonStanding className="w-16 h-16 text-primary-400" />
        </div>
      )}

      {/* Image Viewer Lightbox */}
      {exercise.photoUrl && (
        <ImageViewer
          src={exercise.photoUrl}
          alt={exercise.name}
          isOpen={showImageViewer}
          onClose={() => setShowImageViewer(false)}
        />
      )}

      <div className="flex items-start justify-between p-2">
        <div className="flex-1">
          <div className="font-semibold text-gray-900 dark:text-gray-100">{exercise.name}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{exercise.muscleGroup}</div>
          {subInfo && (
            <div className="text-xs text-primary-600 dark:text-primary-400">
              {subInfo.icon} {subInfo.label}
            </div>
          )}
        </div>
        <div className="flex gap-1">
          <button
            onClick={loadStats}
            className={`p-1.5 rounded-lg transition-colors ${showStats ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400' : 'text-gray-400 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            title="Statisztika"
          >
            {loadingStats ? (
              <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            ) : (
              <BarChart3 className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={onEdit}
            className="p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Description Toggle Button */}
      {hasDescription && (
        <div className="border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={() => setShowDescription(!showDescription)}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
          >
            <span>{showDescription ? 'Leírás elrejtése' : 'Leírás'}</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${showDescription ? 'rotate-180' : ''}`} />
          </button>
        </div>
      )}

      {/* Description Panel */}
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: showDescription ? '400px' : '0px' }}
      >
        <div className="px-3 py-2.5 bg-gradient-to-r from-blue-50 to-white dark:from-gray-750 dark:to-gray-800 border-t border-gray-100 dark:border-gray-700 space-y-2">
          {exercise.description && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">Bemutató</div>
              <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">{exercise.description}</p>
            </div>
          )}
          {exercise.exerciseDescription && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">Gyakorlat</div>
              <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">{exercise.exerciseDescription}</p>
            </div>
          )}
        </div>
      </div>

      {/* Stats Panel */}
      {showStats && stats && (
        <div className="px-2 py-2 bg-gradient-to-r from-primary-50 to-white dark:from-gray-700 dark:to-gray-800 border-t border-gray-200 dark:border-gray-600">
          <ExerciseStatsPanel
            stats={stats}
            onClose={() => setShowStats(false)}
            emptyMessage="Még nincs edzésed ezzel a gyakorlattal."
          />
        </div>
      )}
    </div>
  );
};

export default BodyweightGridCard;
